import { useAuthState } from '@auth/hooks';
import { useSettingsDialog } from '@core/hooks/useSettingsDialog';
import { useTheme } from '@core/hooks/useTheme';
import { useUserPreferences } from '@core/hooks/useUserPreferences';
import { useStyleProvider } from '@core/providers/StyleProvider';
import { Dialog, Select, Switch, Button, ButtonType, type Theme } from '@soteria/design-system-ve';
import { useState } from 'react';

import { AccountTab } from './AccountTab';
import { FeatureFlagSettings } from './FeatureFlagSettings';
import {
  settingsOptionContainer,
  settingsOptionLabel,
  settingsOptionValue,
  selectContainer,
  defaultContentContainer,
  settingsDialogContainer,
  tabbedContainer,
  sidebar,
  sidebarTitle,
  menuItem,
  content,
} from './SettingsDialog.css';
import { SystemTab } from './SystemTab';

export interface SettingsDialogProps {
  /**
   * Whether the dialog is open
   */
  isOpen: boolean;

  /**
   * Callback when the dialog is closed
   */
  onClose: () => void;
}

/**
 * Settings dialog component that displays a settings dialog with menu options.
 * Designed to match the Xbox.com/play settings dialog style.
 *
 * This component can be controlled either through props or through Redux state.
 */
export function SettingsDialog({ isOpen: isOpenProp, onClose: onCloseProp }: SettingsDialogProps) {
  const [activeItemId, setActiveItemId] = useState('account');

  // Use our custom hook to manage the settings dialog
  const { isOpen: isOpenRedux, closeDialog } = useSettingsDialog();

  // Use either the prop value or the Redux value
  const isOpen = isOpenProp !== undefined ? isOpenProp : isOpenRedux;

  // Use either the prop callback or the Redux callback
  const onClose = () => {
    if (onCloseProp) {
      onCloseProp();
    }

    closeDialog();
  };

  // Use our custom hooks to manage the theme and check admin status
  const { theme, isHighContrast, setTheme, setCompleteTheme, setHighContrast } = useTheme();
  const { setTheme: setVanillaTheme } = useStyleProvider();
  const { isUserSystemAdmin } = useAuthState();
  const { homePage, clearHomePage } = useUserPreferences();

  // Determine base theme
  const baseTheme = theme as 'light' | 'dark';

  // Save theme to localStorage and update Redux state whenever it changes
  const updateTheme = (newTheme: Theme) => {
    // Update the vanilla-extract theme and the complete theme in Redux
    setVanillaTheme(newTheme);
    setCompleteTheme(newTheme);

    // Update the theme name in Redux (light, dark, or system)
    const themeName = newTheme.includes('hc-') ? (newTheme.replace('hc-', '') as 'light' | 'dark') : (newTheme as 'light' | 'dark');

    setTheme(themeName);

    // Also save to localStorage for persistence
    localStorage.setItem('theme', newTheme);
  };

  // Define the menu items for the dialog (only show Feature Flags for admins)
  const menuItems = [
    { id: 'account', label: 'Account' },
    { id: 'accessibility', label: 'Accessibility' },
    { id: 'navigation', label: 'Navigation' },
    { id: 'system', label: 'System' },
    ...(isUserSystemAdmin ? [{ id: 'feature flags', label: 'Feature Flags' }] : []),
  ];

  // Render the appropriate content based on the active menu item
  const renderContent = () => {
    switch (activeItemId) {
      case 'account':
        return <AccountTab key="account" className="p-4" />;
      case 'accessibility':
        return (
          <div key="accessibility">
            {/* Theme selection */}
            <div className={settingsOptionContainer}>
              <div className={settingsOptionLabel}>Theme</div>
              <div className={selectContainer}>
                <Select
                  options={[
                    { value: 'light', label: 'Light' },
                    { value: 'dark', label: 'Dark' },
                  ]}
                  value={baseTheme}
                  onChange={value => {
                    const newBaseTheme = value as 'light' | 'dark';

                    // Apply the theme, preserving high contrast setting
                    const newTheme = isHighContrast ? (`hc-${newBaseTheme}` as Theme) : newBaseTheme;

                    updateTheme(newTheme);
                  }}
                />
              </div>
            </div>
            {/* High contrast toggle */}
            <div className={settingsOptionContainer}>
              <div className={settingsOptionLabel}>Enable high contrast</div>
              <Switch
                checked={isHighContrast}
                label="Enable high contrast"
                onChange={enabled => {
                  // Update high contrast state in Redux
                  setHighContrast(enabled);

                  // Apply high contrast setting while preserving base theme
                  const newTheme = enabled ? (`hc-${baseTheme}` as Theme) : baseTheme;

                  // Update the vanilla-extract theme and save to localStorage
                  setVanillaTheme(newTheme);
                  setCompleteTheme(newTheme);
                  localStorage.setItem('theme', newTheme);
                }}
              />
            </div>
          </div>
        );
      case 'feature flags':
        return <FeatureFlagSettings />;
      case 'navigation':
        return (
          <div key="navigation">
            {/* Home Page setting */}
            <div className={settingsOptionContainer}>
              <div>
                <div className={settingsOptionLabel}>Home Page</div>
                <div className={settingsOptionValue}>{homePage || 'Default (Community Groups)'}</div>
              </div>
              {homePage && <Button buttonType={ButtonType.Tertiary} label="Reset to Default" onClick={() => clearHomePage()} />}
            </div>
          </div>
        );
      case 'system':
        return <SystemTab key="system" className="p-4" />;
      default:
        return (
          <div key="other" className={defaultContentContainer}>
            Content for {menuItems.find(item => item.id === activeItemId)?.label} would go here
          </div>
        );
    }
  };

  return (
    <Dialog className={settingsDialogContainer} isOpen={isOpen} size="xl" onClose={onClose}>
      <div className={tabbedContainer} data-theme={baseTheme}>
        {/* Left sidebar with menu */}
        <nav className={sidebar} role="navigation">
          <h2 className={sidebarTitle} id="settings-title">
            Settings
          </h2>
          <div aria-labelledby="settings-title" role="tablist">
            {menuItems.map(item => (
              <button
                key={item.id}
                aria-controls={`${item.id}-panel`}
                aria-current={item.id === activeItemId ? 'page' : undefined}
                aria-selected={item.id === activeItemId}
                className={menuItem({ isActive: item.id === activeItemId })}
                id={`${item.id}-tab`}
                role="tab"
                onClick={() => setActiveItemId(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </nav>

        {/* Right content area */}
        <div aria-labelledby={`${activeItemId}-tab`} className={content} id={`${activeItemId}-panel`} role="tabpanel">
          {renderContent()}
        </div>
      </div>
    </Dialog>
  );
}
